import { useContext, useEffect, useState } from 'react';

import { TranslatorContext } from '@jutro/locale';

import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';

import { WMICLogger, CONSTANTS } from 'wmic-pe-portals-utils-js';
import { AccountService } from 'wmic-pe-capability-gateway-policy';
import { WMICButton } from 'wmic-pe-components-platform-react';
import { useWizardModals } from 'wmic-pe-portals-wizard-components-ui';

import WMICMDENewEmailModal from './WMICMDENewEmailModal/WMICMDENewEmailModal';
import metadata from './WMICMDEDetailsTable.metadata.json5';
import messages from './WMICMDEDetailsTable.messages';

const WMICMDEDetailsTable = ({ policyVM }) => {
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { showCustom, setWizardLoading } = useWizardModals();
    const { authHeader } = useAuthentication();

    const [namedInsuredPersons, updateNamedInsuredPersons] = useState([]);
    const policyNumber = _.get(policyVM, 'policyNumber.value');

    const getNamedInsuredPersons = () => {
        let niPersons = [];
        const namedInsureds = policyVM?.latestPeriod?.namedInsureds?.value;
        if (namedInsureds) {
            niPersons = namedInsureds.filter(ni => ni.subtype === CONSTANTS.Person);
        }
        return niPersons;
    };

    useEffect(() => {
        updateNamedInsuredPersons(getNamedInsuredPersons());
    }, []);

    const updateMDEData = (isInviteSent, index) => {
        const newNIPersons = [...namedInsuredPersons];
        newNIPersons[index].mdeEnrollment_Ext.inviteSent = isInviteSent;
        updateNamedInsuredPersons(newNIPersons);
    };

    const showMDENewEmailModal = (ni, index) => {
        const namedInsuredVM = viewModelService.create(
            ni,
            'pc',
            'wmic.edge.ca.capabilities.gateway.policy.dto.NamedInsuredDTO'
        );

        showCustom(
            <WMICMDENewEmailModal
                namedInsuredVM={namedInsuredVM}
                policyNumber={policyNumber}
                authHeader={authHeader}
                updateMDEData={updateMDEData}
                index={index}
                viewModelService={viewModelService}
            />
        ).catch(err => {
            WMICLogger.error('Failed to send MDE invite', err);
        });
    };

    const sendMDEInvite = async (ni, index) => {
        setWizardLoading(true);
        await AccountService.sendMDEInvite(
            ni?.mdeEnrollment_Ext?.email,
            false,
            policyNumber,
            ni?.mdeEnrollment_Ext?.contactId,
            authHeader
        )
            .then(isInviteSent => {
                updateMDEData(isInviteSent, index);
            })
            .finally(() => {
                setWizardLoading(false);
            });
    };

    const getEmailCell = ni => ni?.mdeEnrollment_Ext?.email ?? '-';

    const getStatusCell = ni => {
        const enrollmentStatus = ni?.mdeEnrollment_Ext?.enrollmentStatus;
        if (enrollmentStatus === CONSTANTS.YES_NO_WMIC_CODES.YES) {
            return translator(messages.enrolled);
        }

        if (enrollmentStatus === CONSTANTS.YES_NO_WMIC_CODES.NO) {
            return translator(messages.notEnrolled);
        }

        return translator(messages.unavailable);
    };

    const getEnrollmentButton = (ni, index) => {
        const email = ni?.mdeEnrollment_Ext?.email;
        const enrollmentStatus = ni?.mdeEnrollment_Ext?.enrollmentStatus;
        if (enrollmentStatus !== CONSTANTS.YES_NO_WMIC_CODES.YES) {
            if (ni?.mdeEnrollment_Ext?.inviteSent) {
                return translator(messages.inviteSent);
            } else {
                return (
                    <WMICButton
                        type="secondary-outlined"
                        onClick={() => {
                            email ? sendMDEInvite(ni, index) : showMDENewEmailModal(ni, index);
                        }}
                        disabled={!enrollmentStatus}
                    >
                        {translator(messages.sendInviteButton)}
                    </WMICButton>
                );
            }
        }
    };

    const overrideProps = {
        '@field': {
            parentNode: policyVM,
            readOnly: true,
        },
        mdeEnrollmentTable: {
            data: namedInsuredPersons,
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            getEmailCell,
            getStatusCell,
            getEnrollmentButton,
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={policyVM}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
};

export default WMICMDEDetailsTable;
